import { useEffect, useState } from 'react';
import { LuLandmark, LuChevronDown } from 'react-icons/lu';
import PayzenCardForm from './PayzenCardForm';
import PaymentErrorRetry from './PaymentErrorRetry';
import PseBankSheet from './PseBankSheet';
import { requestFormToken } from '@/utils/payzenFormToken';
import type { BillingInformation } from './BillingInfoForm';

/**
 * PSE con selección de banco DENTRO del checkout — UN SOLO botón naranja «Pagar con PSE».
 *
 * El comprador elige su banco → PREPARAMOS en segundo plano el formToken
 * (paymentMethods:["PSE"] + acquirerTransientData.PSE.financialInstitutionCode:[banco]) y
 * montamos el SmartForm de Krypton. El botón «Pagar con PSE» que ve el usuario ES el botón
 * NATIVO del SmartForm de Krypton, re-estilizado como nuestro botón naranja — por eso el
 * pago funciona exactamente igual que el botón nativo (y conserva el launcher «Click para
 * ir al pago» de Lyra). Mientras se prepara, un overlay «Preparando pago…» cubre el botón.
 *
 * Por qué NO ocultamos el form + KR.submit(): el smart button de PSE de Krypton se renderiza
 * de forma perezosa según su visibilidad; con el form oculto queda como skeleton y KR.submit()
 * no envía PSE (→ PSP_610 / «Procesando…» colgado). Por eso lo renderizamos en flujo (visible,
 * solo cubierto por nuestro botón) y reenviamos el clic al botón nativo ya pintado.
 *
 * Con un único banco, PayZen OMITE su pantalla de selección y va directo al banco.
 * Reusa el mismo orderId/importe/referencia (no crea orden nueva). La tarjeta no se toca.
 */
function parseIdType(idType: string): string {
  switch (idType) {
    case 'CITIZENSHIP_CARD': return 'CC';
    case 'RESIDENCE_PERMIT': return 'CE';
    case 'EMPRESA': return 'NIT';
    default: return 'CC';
  }
}

type Props = {
  billingInfo: BillingInformation;
  orderId: string; // = payment.reference existente (se reutiliza)
  eventId: string;
  spaceId?: string;
  entranceId?: string;
  timeSlotId?: string;
  quantity: number;
  isSpanish?: boolean;
};

const PsePaymentPanel = ({
  billingInfo, orderId, eventId, spaceId, entranceId, timeSlotId, quantity, isSpanish = true,
}: Props) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [bank, setBank] = useState<{ code: string; name: string } | null>(null);
  const [email, setEmail] = useState(billingInfo.email || '');
  const [editEmail, setEditEmail] = useState(false);

  // Token PREPARADO en segundo plano para (banco, correo) actuales. Se invalida al cambiarlos.
  const [pseToken, setPseToken] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false); // minteo del token en curso
  const [krReady, setKrReady] = useState(false);      // SmartForm Krypton listo (botón nativo pintado)
  const [error, setError] = useState<string | null>(null);

  const buildPayload = () => ({
    orderId, // reutiliza la referencia existente -> sin orden duplicada
    email,
    eventId,
    spaceId,
    entranceId,
    timeSlotId,
    quantity,
    billingInfo,
    // PSE + banco preseleccionado (mecanismo oficial PayZen)
    paymentMethods: ['PSE'],
    acquirerTransientData: { PSE: { financialInstitutionCode: [bank!.code] } },
    customer: {
      email,
      billingDetails: {
        category: billingInfo.idType === 'EMPRESA' ? 'COMPANY' : 'PRIVATE',
        identityType: parseIdType(billingInfo.idType || ''),
        identityCode: billingInfo.idNumber,
        firstName: billingInfo.firstName,
        lastName: billingInfo.lastName,
        phoneNumber: billingInfo.phoneNumber?.number,
        cellPhoneNumber: billingInfo.phoneNumber?.number,
        address: billingInfo.address,
        city: 'Bogota',
        state: 'Cundinamarca',
        country: 'CO',
        zipCode: '110221',
        language: 'es',
      },
    },
  });

  // PREPARACIÓN del token al elegir/cambiar banco o correo (con debounce para el tecleo del
  // correo). Invalida el token anterior (evita reutilización incorrecta) y monta un Krypton
  // nuevo. `cancelled` descarta respuestas obsoletas.
  useEffect(() => {
    setPseToken(null);
    setKrReady(false);
    if (!bank) {
      setPreparing(false);
      return;
    }
    let cancelled = false;
    setError(null);
    setPreparing(true);
    const timer = setTimeout(async () => {
      const outcome = await requestFormToken(fetch, buildPayload());
      if (cancelled) return;
      setPreparing(false);
      if (outcome.formToken) setPseToken(outcome.formToken);
      else
        setError(
          outcome.error?.message ??
            (isSpanish ? 'No se pudo preparar el pago. Intenta de nuevo.' : 'Could not prepare payment. Try again.')
        );
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // email/bank drive the token; buildPayload reads the current values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bank, email]);

  const retryPrepare = () => {
    if (!bank) return;
    setError(null);
    setKrReady(false);
    setPseToken(null);
    setPreparing(true);
    requestFormToken(fetch, buildPayload()).then((o) => {
      setPreparing(false);
      if (o.formToken) setPseToken(o.formToken);
      else
        setError(
          o.error?.message ??
            (isSpanish ? 'No se pudo preparar el pago. Intenta de nuevo.' : 'Could not prepare payment. Try again.')
        );
    });
  };

  // Nuestro botón naranja está SIEMPRE encima del SmartForm nativo y le reenvía el clic;
  // mientras Krypton no está listo (o se re-prepara el token), se muestra «Preparando pago…».
  const showPreparing = !!bank && !error && (preparing || !pseToken || !krReady);

  // Reenvía el clic al botón nativo de Krypton (el que SÍ envía PSE y abre el launcher de
  // Lyra). No usamos KR.submit() porque con el form oculto el smart button queda skeleton y
  // no envía PSE (→ PSP_610). Aquí el form está renderizado (en flujo), solo cubierto.
  const forwardToNative = () => {
    if (showPreparing || typeof document === 'undefined') return;
    const btn = document.querySelector<HTMLElement>(
      '.tikipal-pse-native .kr-smart-button:not(.kr-form-error)'
    );
    if (btn) btn.click();
  };

  return (
    <div className="pt-1">
      <label className="block text-[13px] font-medium text-[#151515a3] mb-1">
        {isSpanish ? 'Selecciona tu banco' : 'Select your bank'}
      </label>
      {/* Selector COMPACTO: una sola fila. Abre el bottom sheet. Al elegir, muestra el
          nombre del banco; tocar de nuevo permite cambiarlo. */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="w-full h-[48px] flex items-center gap-2.5 rounded-[12px] border border-gray-200/80 bg-white px-3.5 text-left transition-colors hover:border-gray-300 focus:outline-none focus:border-tikipal-orange"
      >
        <LuLandmark size={18} className={`shrink-0 ${bank ? 'text-tikipal-orange' : 'text-gray-400'}`} />
        <span className={`flex-1 min-w-0 truncate text-[15px] ${bank ? 'text-slate-800' : 'text-gray-400'}`}>
          {bank ? bank.name : (isSpanish ? 'Escoge tu banco' : 'Choose your bank')}
        </span>
        <LuChevronDown size={18} className="shrink-0 text-gray-400" />
      </button>

      {/* Correo ya diligenciado (reutilizado). Editable; cambiarlo re-prepara el token. */}
      <div className="mt-3 flex items-center gap-2 text-[13px] text-slate-700 bg-[#f0fdf4] border border-[#bbf7d0] rounded-[10px] px-3 py-2">
        {editEmail ? (
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 h-[34px] rounded-[8px] border border-gray-200 px-2 text-[14px]"
          />
        ) : (
          <span className="flex-1 truncate">✓ Correo: <b>{email}</b></span>
        )}
        <button
          type="button"
          onClick={() => setEditEmail((v) => !v)}
          className="text-[12px] text-copper font-semibold hover:underline"
        >
          {editEmail ? 'Listo' : (isSpanish ? 'Editar' : 'Edit')}
        </button>
      </div>
      <p className="text-[11.5px] text-slate-500 mt-1.5">
        {isSpanish
          ? 'Usamos los datos de facturación que ya ingresaste.'
          : 'We use the billing details you already entered.'}
      </p>

      {/* Zona del botón único «Pagar con PSE». */}
      {error ? (
        <div className="mt-2">
          <PaymentErrorRetry message={error} onRetry={retryPrepare} retrying={preparing} isSpanish={isSpanish} />
        </div>
      ) : !bank ? (
        // Sin banco aún: botón deshabilitado con el MISMO estilo que «Comprar»
        // (#billing-info-form-submit). Invita a elegir banco.
        <button
          type="button"
          disabled
          className="w-full h-[44px] mt-1.5 rounded-[12px] bg-tikipal-orange text-white text-[14px] font-semibold transition-colors disabled:opacity-60 flex items-center justify-center"
        >
          {isSpanish ? 'Pagar con PSE' : 'Pay with PSE'}
        </button>
      ) : (
        // Banco elegido: el SmartForm nativo de Krypton se renderiza EN FLUJO (para que su
        // botón PSE se pinte y funcione), y NUESTRO botón naranja lo cubre por completo y le
        // reenvía el clic. El usuario solo ve el botón naranja; nunca el loader/skeleton de
        // Krypton. Mientras no está listo, el botón muestra «Preparando pago…» (deshabilitado).
        <div className="tikipal-pse-native isolate relative mt-1.5 h-[44px]">
          {pseToken && (
            <PayzenCardForm
              formToken={pseToken}
              displayMode="smart-expanded"
              onReady={() => setKrReady(true)}
            />
          )}
          <button
            type="button"
            onClick={forwardToNative}
            disabled={showPreparing}
            aria-busy={showPreparing}
            className="absolute inset-0 z-20 w-full h-[44px] rounded-[12px] bg-tikipal-orange text-white text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-copper-600 active:bg-copper-700 disabled:cursor-wait"
          >
            {showPreparing && (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            {showPreparing
              ? (isSpanish ? 'Preparando pago…' : 'Preparing payment…')
              : (isSpanish ? 'Pagar con PSE' : 'Pay with PSE')}
          </button>
          {/* El SmartForm nativo vive DETRÁS del botón naranja. Lo mantenemos compacto y
              ocultamos su loader/skeleton (nuestro botón ya cubre el estado de carga).
              Scoped a .tikipal-pse-native → no afecta el checkout de tarjeta. */}
          <style>{`
            /* isolation: atrapa el z-index:9999 interno del SmartForm de Krypton dentro de
               #myPaymentForm, para que nuestro botón naranja (z-20) quede por encima. El
               launcher de Lyra se adjunta al body (fuera de aquí) → sigue visible al pagar. */
            .tikipal-pse-native #myPaymentForm { min-height: 0 !important; height: auto !important; isolation: isolate; }
            .tikipal-pse-native .kr-smart-form { min-height: 0 !important; overflow: visible !important; }
            .tikipal-pse-native .kr-smart-button-wrapper { margin: 0 !important; width: 100% !important; }
            .tikipal-pse-native .kr-smart-button:not(.kr-form-error) { width: 100% !important; height: 44px !important; min-height: 44px !important; border-radius: 12px !important; }
            /* Loader interno blanco de PayzenCardForm dentro del panel PSE: lo ocultamos
               (nuestro botón "Preparando pago…" ya cubre la carga). */
            .tikipal-pse-native [class~="bg-white"][class~="inset-0"] { display: none !important; }
          `}</style>
        </div>
      )}

      {/* Bottom sheet de selección (portal a document.body). Selección única: al tocar un
          banco se guarda el código real, se cierra y el useEffect regenera el formToken. */}
      <PseBankSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        selectedCode={bank?.code}
        onSelect={(b) => {
          setBank(b);
          setSheetOpen(false);
        }}
        isSpanish={isSpanish}
      />
    </div>
  );
};

export default PsePaymentPanel;
